// Pattern: docs/design-patterns/common/dry-principle.md (single nav source of truth)
// Pattern: docs/design-patterns/anti-patterns/stringly-typed.md (typed NavItemId literals)
// Pattern: docs/design-patterns/anti-patterns/over-abstraction.md (concrete config, no framework)
//
// Typed nav model for the global shell: Workspace (Dashboard plus Products —
// Projects/Repositories are reached as tabs inside a Product, per the
// D-review decision), Knowledge (doc slugs sourced from Documentation/navigation.ts so
// a slug rename propagates here automatically), and Account (Organization,
// Settings, plus a dev-only Debug Config). `getNavSections` is a function
// (not a static array) so the dev-only item reflects the environment at call
// time — see navConfig.test.tsx.
import type { ReactNode } from 'react';
import {
  BeakerIcon,
  BookOpenIcon,
  BuildingOffice2Icon,
  SwatchIcon,
  UserCircleIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import { Bot, Boxes, LayoutDashboard } from 'lucide-react';

import { getEnvironment } from '~/config';

import { docsNavigation, getBaseSlug } from '~/pages/Documentation/navigation';
import type { NavSection as DocsNavSection } from '~/pages/Documentation/navigation';

/** Stable identifiers for every sidebar item — see anti-patterns/stringly-typed.md. */
export type NavItemId =
  | 'account-agent-preferences'
  | 'account-debug-config'
  | 'account-organization'
  | 'account-settings'
  | 'dashboard'
  | 'knowledge-beta'
  | 'knowledge-how-to-use'
  | 'knowledge-patterns'
  | 'knowledge-reference'
  | 'knowledge-troubleshooting'
  | 'knowledge-what-is-compounds'
  | 'products';

export interface NavItem {
  id: NavItemId;
  label: string;
  to: string;
  icon: ReactNode;
  /**
   * Path prefix (or exact path, with `end`) used to decide whether this item
   * is "active", when it differs from `to` itself — e.g. Settings links to
   * `/settings/profile` but stays active for any `/settings/*` route.
   * Defaults to `to` when omitted.
   */
  activeMatch?: string;
  /** Exact-match instead of prefix-match (mirrors NavLink's `end`). */
  end?: boolean;
}

export interface NavSection {
  id: string;
  title: string;
  items: NavItem[];
}

/** The `/docs/*` section ids every Knowledge item below resolves through `docRoute`. */
export const KNOWLEDGE_DOC_SECTION_IDS = [
  'getting-started',
  'what-is-compounds',
  'how-to-use',
  'reference',
  'troubleshooting',
] as const;

const ICON_CLASS = 'h-5 w-5';

/**
 * Resolves a Knowledge item's route from `docsNavigation` by section id
 * (not a hardcoded path) so a slug rename in navigation.ts propagates here
 * automatically. Falls back to `/docs/<id>` if the id is ever removed —
 * covered by the slug-drift assertion in navConfig.test.tsx, which fails
 * loudly rather than silently linking to a stale path.
 */
function docRoute(docSectionId: string): string {
  const section = findDocSectionById(docsNavigation, docSectionId);
  return `/docs/${section ? getBaseSlug(section.slug) : docSectionId}`;
}

/**
 * Depth-first lookup of a Documentation nav section by its stable `id`
 * (`docsNavigation` nests doc sections under collapsible parents).
 */
function findDocSectionById(sections: DocsNavSection[], id: string): DocsNavSection | undefined {
  for (const section of sections) {
    if (section.id === id) {
      return section;
    }
    if (section.children) {
      const found = findDocSectionById(section.children, id);
      if (found) {
        return found;
      }
    }
  }
  return undefined;
}

const workspaceSection: NavSection = {
  id: 'workspace',
  title: 'Workspace',
  items: [
    {
      id: 'dashboard',
      label: 'Dashboard',
      to: '/dashboard',
      icon: <LayoutDashboard className={ICON_CLASS} />,
    },
    {
      id: 'products',
      label: 'Products',
      to: '/products',
      icon: <Boxes className={ICON_CLASS} />,
    },
  ],
};

const knowledgeSection: NavSection = {
  id: 'knowledge',
  title: 'Knowledge',
  items: [
    {
      id: 'knowledge-beta',
      label: 'Getting Started',
      to: docRoute('getting-started'),
      icon: <BeakerIcon className={ICON_CLASS} />,
      end: true,
    },
    {
      id: 'knowledge-what-is-compounds',
      label: 'What is Compounds?',
      to: docRoute('what-is-compounds'),
      icon: <BookOpenIcon className={ICON_CLASS} />,
      end: true,
    },
    {
      id: 'knowledge-how-to-use',
      label: 'How to Use',
      to: docRoute('how-to-use'),
      icon: <BookOpenIcon className={ICON_CLASS} />,
      end: true,
    },
    {
      id: 'knowledge-reference',
      label: 'Workflow Reference',
      to: docRoute('reference'),
      icon: <BookOpenIcon className={ICON_CLASS} />,
      end: true,
    },
    {
      id: 'knowledge-troubleshooting',
      label: 'Troubleshooting',
      to: docRoute('troubleshooting'),
      icon: <BookOpenIcon className={ICON_CLASS} />,
      end: true,
    },
    {
      id: 'knowledge-patterns',
      label: 'Pattern Library',
      to: '/patterns',
      icon: <SwatchIcon className={ICON_CLASS} />,
    },
  ],
};

/** Account section, including the dev-only Debug Config item. */
function buildAccountSection(): NavSection {
  const { isDevelopment } = getEnvironment();

  return {
    id: 'account',
    title: 'Account',
    items: [
      {
        id: 'account-organization',
        label: 'Organization',
        to: '/org/settings',
        activeMatch: '/org',
        icon: <BuildingOffice2Icon className={ICON_CLASS} />,
      },
      {
        id: 'account-settings',
        label: 'Settings',
        to: '/settings/profile',
        activeMatch: '/settings',
        icon: <UserCircleIcon className={ICON_CLASS} />,
      },
      {
        id: 'account-agent-preferences',
        label: 'Agent Preferences',
        to: '/agent-preferences',
        icon: <Bot className={ICON_CLASS} />,
      },
      ...(isDevelopment
        ? [
            {
              id: 'account-debug-config' as const,
              label: 'Debug Config',
              to: '/debug/config',
              icon: <WrenchScrewdriverIcon className={ICON_CLASS} />,
            },
          ]
        : []),
    ],
  };
}

/** The active item across every section, if the current route matches one. */
export function findActiveNavItem(sections: NavSection[], pathname: string): NavItem | undefined {
  for (const section of sections) {
    const match = section.items.find(item => isNavItemActive(item, pathname));
    if (match) {
      return match;
    }
  }
  return undefined;
}

/**
 * Builds the global shell's nav sections: Workspace, Knowledge, Account.
 * A function rather than a static export so the dev-only Debug Config item
 * is (re)computed from the current environment on every call.
 */
export function getNavSections(): NavSection[] {
  return [workspaceSection, knowledgeSection, buildAccountSection()];
}

/**
 * Single source of truth for "is this nav item active" (SidebarNav's
 * highlighting and TopBar's route-derived title both need it) — see
 * dry-principle.md. Matches against `activeMatch` when given (it can differ
 * from `to`, e.g. Settings links to /settings/profile but stays active for
 * any /settings/* route); `end` requests exact- instead of prefix-match.
 */
export function isNavItemActive(item: NavItem, pathname: string): boolean {
  const base = item.activeMatch ?? item.to;

  if (item.end) {
    return pathname === base;
  }

  return pathname === base || pathname.startsWith(`${base}/`);
}
